// Picks one more person to pull into an ongoing call. Lists sales colleagues and
// (where available) customers; on select, rings them into the same room.
import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";

import { listSalesUsers, listChatCustomers, adminDirectory } from "../../../redux/apis/chatAPIs";
import { addToCall } from "../../../redux/apis/callApis";
import { initials, avatarColor } from "../../../pages/chat/chatHelpers";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";

interface Person {
  key: string;
  type: "sales" | "retailer";
  id: number;
  name: string;
  sub?: string;
}

interface Props {
  open: boolean;
  callId: number;
  onClose: () => void;
}

const AddToCallDialog: React.FC<Props> = ({ open, callId, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [people, setPeople] = useState<Person[]>([]);
  const [dirPeople, setDirPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  // The small, non-searchable sources (sales list + the caller's own customers),
  // fetched once and filtered locally.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDirPeople([]);
    let cancelled = false;
    Promise.allSettled([listSalesUsers(), listChatCustomers()]).then(([sales, customers]) => {
      if (cancelled) return;
      const list: Person[] = [];
      if (sales.status === "fulfilled") {
        for (const u of sales.value) {
          list.push({
            key: `sales:${u.id}`,
            type: "sales",
            id: u.id,
            name: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email || `Sales #${u.id}`,
            sub: "Sales",
          });
        }
      }
      if (customers.status === "fulfilled") {
        for (const c of customers.value) {
          const name = (c.C_CoName || "").trim() || (c.C_Name || "").trim() || `Customer #${c.C_Number}`;
          list.push({ key: `retailer:${c.C_Number}`, type: "retailer", id: c.C_Number, name, sub: "Customer" });
        }
      }
      setPeople(list);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Admin directory is searched SERVER-side (so every customer is reachable, not just
  // the first page). 403s for sales — harmless, they use the local lists.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      adminDirectory(search)
        .then((res) => {
          if (cancelled) return;
          const list: Person[] = [
            ...res.sales.map((s) => ({ key: `sales:${s.id}`, type: "sales" as const, id: s.id, name: s.name, sub: "Sales" })),
            ...res.retailers.map((r) => ({ key: `retailer:${r.id}`, type: "retailer" as const, id: r.id, name: r.name, sub: "Customer" })),
          ];
          setDirPeople(list);
        })
        .catch(() => !cancelled && setDirPeople([]))
        .finally(() => !cancelled && setLoading(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const local = q ? people.filter((p) => p.name.toLowerCase().includes(q)) : people;
    // Merge local (filtered) with the server-searched directory, deduped.
    const byKey = new Map<string, Person>();
    [...local, ...dirPeople].forEach((p) => {
      if (!byKey.has(p.key)) byKey.set(p.key, p);
    });
    return Array.from(byKey.values());
  }, [people, dirPeople, search]);

  const add = async (p: Person) => {
    setBusyId(p.key);
    try {
      await addToCall(callId, { targetType: p.type, targetId: p.id });
      showSuccessToast(`Ringing ${p.name}…`);
      onClose();
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || "Could not add to the call");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      // Above the fullscreen call overlay (zIndex 2000), else it opens behind it.
      sx={{ zIndex: 2200 }}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonAddRoundedIcon color="primary" />
          <span>Add to call</span>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            mb: 1,
            borderRadius: 2.5,
            bgcolor: isDark ? alpha("#fff", 0.06) : "#F1F3F6",
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <InputBase
            fullWidth
            placeholder="Search people…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: 14 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No one to add.
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: 340, overflowY: "auto" }}>
            {filtered.map((p) => (
              <ListItemButton
                key={p.key}
                disabled={busyId === p.key}
                onClick={() => add(p)}
                sx={{ borderRadius: 2, py: 1 }}
              >
                <Avatar sx={{ width: 36, height: 36, mr: 1.5, bgcolor: avatarColor(p.name) }}>
                  {initials(p.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                    {p.name}
                  </Typography>
                </Box>
                <Chip label={p.sub} size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                {busyId === p.key && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddToCallDialog;
